import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { BaseballAverageStatType } from '@sorare/core/src/__generated__/globalTypes';
import { TextSwitch } from '@sorare/core/src/atoms/inputs/TextSwitch';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Switch } from '@sorare/core/src/components/search/Switch';
import { StatsMode } from '@sorare/core/src/lib/playerSearchView';
import { categoryLabels, statLabels } from '@sorare/core/src/lib/scoring';

import {
  hittingBaseballAverageStats,
  pitchingBaseballAverageStats,
} from 'lib/scoutingCenter';

const baseballSortableStats = {
  [StatsMode.HITTING]: hittingBaseballAverageStats.map(stat => ({
    value: stat,
    label: (
      <FormattedMessage
        {...statLabels[stat.toLowerCase() as keyof typeof statLabels]}
      />
    ),
  })),
  [StatsMode.PITCHING]: pitchingBaseballAverageStats.map(stat => ({
    value: stat,
    label: (
      <FormattedMessage
        {...statLabels[stat.toLowerCase() as keyof typeof statLabels]}
      />
    ),
  })),
  [StatsMode.DEFAULT]: [],
};

const CapitalizeFirstLetter = styled.span`
  &::first-letter {
    text-transform: uppercase;
  }
`;

type Props = {
  stats: BaseballAverageStatType[];
  onChange: (sort: BaseballAverageStatType[]) => void;
  statsMode: StatsMode;
  onStatsModeChange: (statsMode: StatsMode) => void;
};

export const BaseballStats = ({
  stats,
  onChange,
  statsMode,
  onStatsModeChange,
}: Props) => {
  return (
    <Vertical>
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage
          id="searchCommonPlayer.statsManager.stats.title"
          defaultMessage="Statistics"
        />
      </LabelM>
      <TextSwitch
        checked={statsMode === StatsMode.HITTING}
        compact
        onChange={() =>
          onStatsModeChange(
            statsMode === StatsMode.HITTING
              ? StatsMode.PITCHING
              : StatsMode.HITTING
          )
        }
        onLabel={
          <CapitalizeFirstLetter>
            <FormattedMessage {...categoryLabels.HITTING} />
          </CapitalizeFirstLetter>
        }
        offLabel={
          <CapitalizeFirstLetter>
            <FormattedMessage {...categoryLabels.PITCHING} />
          </CapitalizeFirstLetter>
        }
      />
      {baseballSortableStats[statsMode]?.map(({ value, label }) => (
        <Switch
          key={value}
          label={
            <Horizontal>
              <CapitalizeFirstLetter>{label}</CapitalizeFirstLetter>
            </Horizontal>
          }
          checked={stats.some(v => v === value)}
          onChange={() => {
            onChange(
              stats.includes(value)
                ? stats.filter(v => v !== value)
                : [...stats, value]
            );
          }}
        />
      ))}
    </Vertical>
  );
};
